import React, { useState } from "react";
import PageTableHeader from "../../../components/pageTableHeader/pageTableHeader";
import CommonTable from "../../../components/table/table";
import { useCommonFetch } from "../../../../hooks/useCommonFetch";
import { useSelector } from "react-redux";
import {
  getMyExpenses,
  getExpenseDetail,
  deleteExpense,
  submitExpense,
} from "../../../../services/workspace/reimbursement.service";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import DefaultButton from "../../../components/buttons/defaultButton";
import CommonModal from "../../../components/modals/commonModel";
import DefaultBadge from "../../../components/badge/defaultBadge";
import { FiUpload, FiTrash2, FiEye } from "react-icons/fi";
import BottomDrawer from "../../common/drawer/bottomDrawer";
import ExpenseSubmissionForm from "../components/ExpenseSubmissionForm";
import { usePermissionCheck } from "../../../../hooks";
import { createProtectedActionButtons } from "../../../../utils/actionButtonHelpers";

const items = [
  { label: "Reimbursement", path: "/reimbursement/my-expenses" },
  { label: "My Expenses", path: "" },
];

const STATUS_DRAFT = "DRAFT";
const STATUS_SUBMITTED = "SUBMITTED";
const STATUS_PENDING_APPROVAL = "PENDING_APPROVAL";
const STATUS_APPROVED = "APPROVED";
const STATUS_REJECTED = "REJECTED";
const STATUS_RETURNED = "RETURNED";
const STATUS_PROCESSING = "PROCESSING";
const STATUS_PAID = "PAID";
const STATUS_ADDED_TO_PAYROLL = "ADDED_TO_PAYROLL";
const ALLOWED_STATUS = [
  STATUS_DRAFT,
  STATUS_SUBMITTED,
  STATUS_PENDING_APPROVAL,
  STATUS_APPROVED,
  STATUS_REJECTED,
  STATUS_RETURNED,
  STATUS_PROCESSING,
  STATUS_PAID,
  STATUS_ADDED_TO_PAYROLL,
];

const MyExpenses = () => {
  const { limit } = useSelector((state) => state.global);
  const navigate = useNavigate();
  
  // Check permissions for reimbursement my-expenses
  const permissions = usePermissionCheck("reimbursement", "my-expenses");
  const { canView, canUpdate, canDelete } = permissions;

  const [modal, setModal] = useState({
    show: false,
    title: "",
    message: "",
    actionType: null,
    item: null,
    loading: false,
  });

  const [drawer, setDrawer] = useState({
    show: false,
    mode: "add", // add or edit
    data: null,
  });

  const {
    loading,
    data: tableData,
    total: totalData,
    currentPage,
    handleSearchChange,
    handlePageChange,
    fetchData,
  } = useCommonFetch(getMyExpenses, {
    dataPath: "data.data",
    totalPath: "data.meta.total",
    errorMessage: "Failed to fetch expenses!",
    debounceDelay: 500,

    dataTransformer: (data) => {
      if (!Array.isArray(data)) return { data: [], action: {} };

      const transformedData = data.map((item) => {
        const incomingStatus = String(item.status || STATUS_DRAFT).toUpperCase();
        const expenseStatus = ALLOWED_STATUS.includes(incomingStatus)
          ? incomingStatus
          : STATUS_DRAFT;
        

        return {
        ...item,
        _raw: item,
        _expenseStatusKey: expenseStatus,
        id: { data: item.uuid, variant: "text" },
        created_at: {
          data: item.created_at || "-",
          variant: item.created_at ? "date-time" : "text",
        },
        expense_number: { 
          data: item.expense_number || "-", 
          variant: "text",
          className: "font-semibold text-primary-600"
        },
        category: { 
          data: item.category?.name || "-", 
          variant: "text",
          className: "font-medium"
        },
        expense_date: {
          data: item.expense_date || "-",
          variant: item.expense_date ? "date" : "text",
        },
        amount: {
          data: item.amount || 0,
          variant: "currency",
          className: "font-semibold text-gray-900"
        },
        vendor: { data: item.vendor || "-", variant: "text" },
        status: {
          data: expenseStatus || "-",
          variant: "text",
        },
        payment_method: {
          data: item.payment_method || "-",
          variant: "text",
          render: () => {
            if (item.payment_method === "INSTANT_PAYOUT") {
              return <DefaultBadge name="Instant Payout" color="green" />;
            } else if (item.payment_method === "PAYROLL") {
              return <DefaultBadge name="Payroll" color="purple" />;
            }
            return <span className="text-gray-500">-</span>;
          },
        },
        submitted_at: {
          data: item.submitted_at || "-",
          variant: item.submitted_at ? "date-time" : "text",
        },
      };
      });

      // Create action buttons with permission checks
      const actionButton = createProtectedActionButtons(
        {
          view: {
            handler: (item) => navigate(`/reimbursement/expenses/${item.id.data}`, { state: { from: 'my-expenses' } }),
            requirePermission: "view",
          },
          edit: {
            handler: async (item) => {
              try {
                const expenseId = item?.id?.data || item?.uuid || item?.id;
                if (!expenseId) return;
                const response = await getExpenseDetail(expenseId);
                const fullExpense = response?.data || response || item?._raw || item;
                setDrawer({ show: true, mode: "edit", data: fullExpense });
              } catch (error) {
                toast.error(
                  error?.response?.data?.message ||
                    "Failed to load expense details for edit"
                );
              }
            },
            requirePermission: "update",
            condition: (item) => {
              const key = item._expenseStatusKey || "";
              return key === STATUS_DRAFT || key === STATUS_RETURNED;
            },
          },
          submit: {
            handler: (item) =>
          setModal({
            show: true,
            actionType: "submit",
            item,
            title: "Submit Expense",
            message: `Submit expense ${item.expense_number.data} for approval?`,
          }),
            requirePermission: "update",
            condition: (item) => {
              const key = item._expenseStatusKey || "";
              return key === STATUS_DRAFT || key === STATUS_RETURNED;
            },
          },
          delete: {
            handler: (item) =>
          setModal({
            show: true,
            actionType: "delete",
            item,
            title: "Delete Expense",
            message: `Are you sure you want to delete expense ${item.expense_number.data}? This action cannot be undone.`,
          }),
            requirePermission: "delete",
            condition: (item) => (item._expenseStatusKey || "") === STATUS_DRAFT,
          },
        },
        permissions
      );

      return { data: transformedData, action: actionButton };
    },
  });

  const handleActionConfirm = async () => {
    if (!modal.item) return;

    setModal((prev) => ({ ...prev, loading: true }));

    try {
      if (modal.actionType === "delete") {
        await deleteExpense(modal.item.id.data);
        toast.success("Expense deleted successfully!");
      } else if (modal.actionType === "submit") {
        await submitExpense(modal.item.id.data);
        toast.success("Expense submitted for approval!");
      }

      fetchData();
      setModal({ show: false, item: null, actionType: null });
    } catch (err) {
      toast.error(
        err?.response?.data?.message || `Failed to ${modal.actionType} expense`
      );
      setModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleFormSuccess = () => {
    setDrawer({ show: false, mode: "add", data: null });
    // Force refresh table after successful form submission
    // Use setTimeout to ensure drawer closes first, then refresh
    setTimeout(() => {
      fetchData({}, true);
    }, 100);
  };

  const TableHeaderData = [
    { label: "Expense ID", key: "expense_number" },
    { label: "Category", key: "category" },
    { label: "Expense Date", key: "expense_date" },
    { label: "Amount", key: "amount" },
    { label: "Vendor", key: "vendor" },
    { label: "Status", key: "status" },
    // { label: "Payment Method", key: "payment_method" },
    { label: "Submitted At", key: "created_at" },
  ];

  return (
    <>
      <div className="flex flex-col gap-4">
        <PageTableHeader
          isSearch
          onSearchChange={handleSearchChange}
          add
          addButtonText="Submit New Expense"
          onClickAdd={() => setDrawer({ show: true, mode: "add", data: null })}
          breadcrumbs={items}
        />

        <div className="bg-white rounded-lg shadow-sm">
          <CommonTable
            TableHeaderData={TableHeaderData}
            tabledata={tableData.data || []}
            currentPage={currentPage}
            totalData={totalData}
            setCurrentPage={handlePageChange}
            loading={loading}
            action={tableData.action}
          />
        </div>
      </div>

      {/* Action Confirmation Modal */}
      <CommonModal
        show={modal.show}
        headerTitle={modal.title}
        onClose={() => setModal({ show: false, item: null })}
        modalSize="md"
      >
        <div className="text-center space-y-6 py-4">
          <p className="text-gray-600 text-base">{modal.message}</p>

          <div className="flex justify-center gap-4 pt-4">
            <DefaultButton
              name="Cancel"
              variant="outline"
              size="md"
              onClick={() => setModal({ show: false, item: null })}
              disabled={modal.loading}
            />

            <DefaultButton
              name="Confirm"
              variant={modal.actionType === "delete" ? "danger" : "primary"}
              size="md"
              loading={modal.loading}
              onClick={handleActionConfirm}
            />
          </div>
        </div>
      </CommonModal>

      {/* Expense Submission Drawer */}
      <BottomDrawer
        isOpen={drawer.show}
        setIsOpen={() => setDrawer({ show: false, mode: "add", data: null })}
        title={drawer.mode === "add" ? "Submit New Expense" : "Edit Expense"}
      >
        <ExpenseSubmissionForm
          mode={drawer.mode}
          initialData={drawer.data}
          onSuccess={handleFormSuccess}
          onCancel={() => setDrawer({ show: false, mode: "add", data: null })}
        />
      </BottomDrawer>
    </>
  );
};

export default MyExpenses;

